import { useCallback, useEffect } from 'react';

import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { CuroApplicationVariables } from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import { useCurrentStep } from 'hooks/useCurrentStep';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import { NotFoundPageContent } from 'pages/CuroPages/NotFoundPage/NotFoundPage';
import {
  runSecondChanceCashflowAndAutoOrigination,
  runApplicationSummitLead,
  updateCurrentApplication,
} from 'thunks';

/* eslint-disable no-restricted-imports */
import { BranchOfferFlowSteps, BranchOfferType } from '../constants';
import {
  BankInformationPlaidIntegrationWaitingStep,
  BankInformationPlaidStep,
  ConnectBankAccountForFundingStep,
  BranchEndStep,
  SelectBranchOfferStep,
} from '../steps';

const BranchOfferFlow = () => {
  const trackEvent = useGoogleAnalytics();
  const application = useAppSelector(getApplicationSelector)!;
  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const [currentStep, setCurrentStep] = useCurrentStep<BranchOfferFlowSteps>(
    BranchOfferFlowSteps.SelectBranchOfferStep,
  );

  const updateEmailsTriggers = useCallback(async () => {
    await dispatchWithUnwrap(
      updateCurrentApplication({
        variables: {
          [CuroApplicationVariables.BranchOfferDisplayedTechnical]: true,
          [CuroApplicationVariables.SendNextStepsEmail]: true,
          [CuroApplicationVariables.SendOfferDetailsEmail]: true,
        },
      }),
    );
  }, []);

  useEffect(() => {
    updateEmailsTriggers();
  }, [updateEmailsTriggers]);

  const onCompletePlaid = async () => {
    await dispatchWithUnwrap(
      runSecondChanceCashflowAndAutoOrigination({ applicationId: application?.id }),
    );
    dispatchWithUnwrap(runApplicationSummitLead({ applicationId: application?.id }));
    setCurrentStep(BranchOfferFlowSteps.CentralBranchOfferStep);
    trackEvent(GoogleAnalyticsEventType.OnlineOrigination);
  };

  const handleFinishAndGoToEndPage = async () => {
    dispatchWithUnwrap(
      updateCurrentApplication({
        variables: {
          [CuroApplicationVariables.SendNextStepsEmail]: true,
        },
      }),
    );
    dispatchWithUnwrap(runApplicationSummitLead({ applicationId: application?.id }));
    setCurrentStep(BranchOfferFlowSteps.CentralBranchOfferStep);
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case BranchOfferFlowSteps.SelectBranchOfferStep:
        return <SelectBranchOfferStep onStepComplete={setCurrentStep} />;
      case BranchOfferFlowSteps.PhysicalBranchOfferStep:
        return <BranchEndStep branchType={BranchOfferType.Physical} />;
      case BranchOfferFlowSteps.CentralBranchOfferStep:
        return <BranchEndStep branchType={BranchOfferType.Central} />;
      case BranchOfferFlowSteps.ConnectBankAccountForFundingStep:
        return (
          <ConnectBankAccountForFundingStep
            onStepComplete={handleFinishAndGoToEndPage}
            onPlaid={() => setCurrentStep(BranchOfferFlowSteps.BankInformationPlaid)}
          />
        );
      case BranchOfferFlowSteps.BankInformationPlaid:
        return (
          <BankInformationPlaidStep
            onStepExit={() => setCurrentStep(BranchOfferFlowSteps.ConnectBankAccountForFundingStep)}
            onStepFailed={() =>
              setCurrentStep(BranchOfferFlowSteps.BankInformationPLaidIntegrationFailed)
            }
            onStepComplete={() =>
              setCurrentStep(BranchOfferFlowSteps.BankInformationPlaidIntegrationWaiting)
            }
          />
        );
      case BranchOfferFlowSteps.BankInformationPlaidIntegrationWaiting:
        return (
          <BankInformationPlaidIntegrationWaitingStep
            onStepFail={handleFinishAndGoToEndPage}
            onStepComplete={onCompletePlaid}
          />
        );
      case BranchOfferFlowSteps.BankInformationPLaidIntegrationFailed:
        return <NotFoundPageContent />;

      default:
        return null as never;
    }
  };

  return renderCurrentStep();
};

export default BranchOfferFlow;
